import { createApp, nextTick } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from './router'
import axios from 'axios'
import ElementPlus from 'element-plus'
import 'element-plus/dist/index.css'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'
import './assets/css/main.css'
import ko from 'element-plus/dist/locale/ko.mjs'
import { useAuthStore } from './stores/auth'

// Chrome + Element Plus 등에서 레이아웃 연속 갱신 시 나오는 무해한 ResizeObserver 알림이
// webpack-dev-server 오버레이만 띄우는 경우가 있어 해당 메시지만 전파 차단
window.addEventListener(
  'error',
  (event) => {
    const msg = event?.message || ''
    if (
      msg.includes('ResizeObserver loop completed with undelivered notifications') ||
      msg.includes('ResizeObserver loop limit exceeded')
    ) {
      event.stopImmediatePropagation()
    }
  },
  true
)

// GET API가 브라우저 휴리스틱 캐시에 남지 않도록 (서버 반영 후 F5만으로 최신 조회)
axios.defaults.headers.common['Cache-Control'] = 'no-cache'
axios.defaults.headers.common['Pragma'] = 'no-cache'

const app = createApp(App)
const pinia = createPinia()

// Element Plus 아이콘 등록
for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
  app.component(key, component)
}

app.use(pinia)

// 라우터 등록 전에 인증 상태 초기화
const authStore = useAuthStore()
authStore.initializeAuth()

// 전역 디렉티브: 물리적 키보드 입력 지원
app.directive('physical-keyboard', {
  mounted(el) {
    const applyAttributes = (inputElement) => {
      if (inputElement && inputElement.tagName === 'INPUT') {
        inputElement.setAttribute('inputmode', 'text')
        inputElement.setAttribute('autocapitalize', 'off')
        inputElement.setAttribute('autocomplete', 'off')
        inputElement.setAttribute('autocorrect', 'off')
        inputElement.setAttribute('spellcheck', 'false')
      }
    }

    const findAndApply = () => {
      // Element Plus input의 내부 input 요소 찾기 (여러 방법 시도)
      let input = el.querySelector('.el-input__wrapper input') ||
                  el.querySelector('.el-input__inner') ||
                  el.querySelector('input')
      
      if (input && input.tagName === 'INPUT') {
        applyAttributes(input)
      } else {
        // DOM이 아직 준비되지 않았을 수 있으므로 MutationObserver 사용
        const observer = new MutationObserver(() => {
          input = el.querySelector('.el-input__wrapper input') ||
                  el.querySelector('.el-input__inner') ||
                  el.querySelector('input')
          if (input && input.tagName === 'INPUT') {
            applyAttributes(input)
            observer.disconnect()
          }
        })
        
        observer.observe(el, {
          childList: true,
          subtree: true
        })
        
        // 1초 후에도 찾지 못하면 observer 해제
        setTimeout(() => {
          observer.disconnect()
        }, 1000)
      }
    }

    // DOM이 완전히 렌더링된 후 처리 (여러 시점에서 시도)
    setTimeout(() => {
      findAndApply()
    }, 0)
    
    // Element Plus가 비동기로 렌더링할 수 있으므로 추가 시도
    setTimeout(() => {
      findAndApply()
    }, 100)
    
    setTimeout(() => {
      findAndApply()
    }, 300)
  },
  updated(el) {
    // 업데이트 시에도 동일하게 적용
    setTimeout(() => {
      const input = el.querySelector('.el-input__wrapper input') ||
                    el.querySelector('.el-input__inner') ||
                    el.querySelector('input')
      if (input && input.tagName === 'INPUT') {
        input.setAttribute('inputmode', 'text')
        input.setAttribute('autocapitalize', 'off')
        input.setAttribute('autocomplete', 'off')
        input.setAttribute('autocorrect', 'off')
        input.setAttribute('spellcheck', 'false')
      }
    }, 0)
  }
})

app.use(router)
app.use(ElementPlus, {
  locale: ko
})
app.mount('#app')
